import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/grid.default.js';
import * as wireframeSkin from './skins/grid.wireframe.js';

export const GRID_ALIGN_VALUES = Object.freeze(['start', 'center', 'end', 'stretch', 'baseline']);
export const GRID_JUSTIFY_VALUES = Object.freeze(['start', 'center', 'end', 'stretch']);
export const GRID_ALIGN_CONTENT_VALUES = Object.freeze(['start', 'center', 'end', 'stretch', 'between', 'around', 'evenly']);
export const GRID_JUSTIFY_CONTENT_VALUES = Object.freeze(['start', 'center', 'end', 'stretch', 'between', 'around', 'evenly']);
export const GRID_PLACE_ITEMS_VALUES = Object.freeze(['start', 'center', 'end', 'stretch']);
export const GRID_FLOW_VALUES = Object.freeze(['row', 'column', 'row-dense', 'column-dense']);
export const GRID_GAP_ALIASES = Object.freeze(['xs', 'sm', 'md', 'lg', 'xl']);

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles || '');

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunGrid extends RunElement {
  // Skin — not in declarative system (must preserve null default, no String coercion)
  #skin = null;

  // Grid container properties
  #columns = '';
  #rows = '';
  #areas = '';
  #gap = '';
  #gapX = '';
  #gapY = '';
  #align = 'stretch';
  #justify = 'start';
  #alignContent = '';
  #justifyContent = '';
  #placeItems = '';
  #autoRows = 'auto';
  #autoColumns = '';
  #flow = 'row';
  #inline = false;

  #rendering = false;

  static get observedAttributes() {
    return [
      'skin',
      'columns', 'rows', 'areas',
      'gap', 'gap-x', 'gap-y',
      'align', 'justify',
      'align-content', 'justify-content', 'place-items',
      'auto-rows', 'auto-columns',
      'flow', 'inline',
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'skin':            this.skin = newValue; break;
      case 'columns':         this.columns = newValue !== null ? newValue : ''; break;
      case 'rows':            this.rows = newValue !== null ? newValue : ''; break;
      case 'areas':           this.areas = newValue !== null ? newValue : ''; break;
      case 'gap':             this.gap = newValue !== null ? newValue : ''; break;
      case 'gap-x':           this.gapX = newValue !== null ? newValue : ''; break;
      case 'gap-y':           this.gapY = newValue !== null ? newValue : ''; break;
      case 'align':           this.align = newValue !== null ? newValue : 'stretch'; break;
      case 'justify':         this.justify = newValue !== null ? newValue : 'start'; break;
      case 'align-content':   this.alignContent = newValue !== null ? newValue : ''; break;
      case 'justify-content': this.justifyContent = newValue !== null ? newValue : ''; break;
      case 'place-items':     this.placeItems = newValue !== null ? newValue : ''; break;
      case 'auto-rows':       this.autoRows = newValue !== null ? newValue : 'auto'; break;
      case 'auto-columns':    this.autoColumns = newValue !== null ? newValue : ''; break;
      case 'flow':            this.flow = newValue !== null ? newValue : 'row'; break;
      case 'inline':          this.inline = newValue !== null; break;
    }
  }

  // ── skin ──────────────────────────────────────────────────────────────────
  get skin() { return this.#skin; }
  set skin(value) {
    const coerced = value === null || value === undefined ? null : String(value);
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced !== null) {
      this.setAttribute('skin', coerced);
    } else {
      this.removeAttribute('skin');
    }
    this.requestUpdate();
  }

  // ── columns ───────────────────────────────────────────────────────────────
  get columns() { return this.#columns; }
  set columns(value) {
    const coerced = value == null ? '' : String(value);
    if (this.#columns === coerced) return;
    this.#columns = coerced;
    if (coerced) { this.setAttribute('columns', coerced); } else { this.removeAttribute('columns'); }
    this.requestUpdate();
  }

  get _resolvedColumns() {
    const cols = this.#columns;
    if (!cols) return '';
    const n = Number(cols);
    if (!isNaN(n)) {
      return `repeat(${Math.max(1, Math.floor(n))}, 1fr)`;
    }
    return cols;
  }

  // ── rows ──────────────────────────────────────────────────────────────────
  get rows() { return this.#rows; }
  set rows(value) {
    const coerced = value == null ? '' : String(value);
    if (this.#rows === coerced) return;
    this.#rows = coerced;
    if (coerced) { this.setAttribute('rows', coerced); } else { this.removeAttribute('rows'); }
    this.requestUpdate();
  }

  // ── areas ─────────────────────────────────────────────────────────────────
  get areas() { return this.#areas; }
  set areas(value) {
    const coerced = value == null ? '' : String(value);
    if (this.#areas === coerced) return;
    this.#areas = coerced;
    if (coerced) { this.setAttribute('areas', coerced); } else { this.removeAttribute('areas'); }
    this.requestUpdate();
  }

  // ── gap ───────────────────────────────────────────────────────────────────
  get gap() { return this.#gap; }
  set gap(value) {
    const coerced = value == null ? '' : String(value);
    if (this.#gap === coerced) return;
    this.#gap = coerced;
    if (coerced) { this.setAttribute('gap', coerced); } else { this.removeAttribute('gap'); }
    this.requestUpdate();
  }

  get gapX() { return this.#gapX; }
  set gapX(value) {
    const coerced = value == null ? '' : String(value);
    if (this.#gapX === coerced) return;
    this.#gapX = coerced;
    if (coerced) { this.setAttribute('gap-x', coerced); } else { this.removeAttribute('gap-x'); }
    this.requestUpdate();
  }

  get gapY() { return this.#gapY; }
  set gapY(value) {
    const coerced = value == null ? '' : String(value);
    if (this.#gapY === coerced) return;
    this.#gapY = coerced;
    if (coerced) { this.setAttribute('gap-y', coerced); } else { this.removeAttribute('gap-y'); }
    this.requestUpdate();
  }

  get _resolvedGap()  { return _resolveGapValue(this.#gap); }
  get _resolvedGapX() { return _resolveGapValue(this.#gapX); }
  get _resolvedGapY() { return _resolveGapValue(this.#gapY); }

  // ── align / justify ───────────────────────────────────────────────────────
  get align() { return this.#align; }
  set align(value) {
    const raw = value == null ? '' : String(value);
    const coerced = GRID_ALIGN_VALUES.includes(raw) ? raw : 'stretch';
    if (this.#align === coerced) return;
    this.#align = coerced;
    this.setAttribute('align', coerced);
    this.requestUpdate();
  }

  get justify() { return this.#justify; }
  set justify(value) {
    const raw = value == null ? '' : String(value);
    const coerced = GRID_JUSTIFY_VALUES.includes(raw) ? raw : 'start';
    if (this.#justify === coerced) return;
    this.#justify = coerced;
    this.setAttribute('justify', coerced);
    this.requestUpdate();
  }

  get alignContent() { return this.#alignContent; }
  set alignContent(value) {
    const raw = value == null ? '' : String(value);
    const coerced = GRID_ALIGN_CONTENT_VALUES.includes(raw) ? raw : '';
    if (this.#alignContent === coerced) return;
    this.#alignContent = coerced;
    if (coerced) { this.setAttribute('align-content', coerced); } else { this.removeAttribute('align-content'); }
    this.requestUpdate();
  }

  get justifyContent() { return this.#justifyContent; }
  set justifyContent(value) {
    const raw = value == null ? '' : String(value);
    const coerced = GRID_JUSTIFY_CONTENT_VALUES.includes(raw) ? raw : '';
    if (this.#justifyContent === coerced) return;
    this.#justifyContent = coerced;
    if (coerced) { this.setAttribute('justify-content', coerced); } else { this.removeAttribute('justify-content'); }
    this.requestUpdate();
  }

  get placeItems() { return this.#placeItems; }
  set placeItems(value) {
    const raw = value == null ? '' : String(value);
    const coerced = GRID_PLACE_ITEMS_VALUES.includes(raw) ? raw : '';
    if (this.#placeItems === coerced) return;
    this.#placeItems = coerced;
    if (coerced) { this.setAttribute('place-items', coerced); } else { this.removeAttribute('place-items'); }
    this.requestUpdate();
  }

  // ── auto-rows / auto-columns ──────────────────────────────────────────────
  get autoRows() { return this.#autoRows; }
  set autoRows(value) {
    const coerced = value == null ? 'auto' : String(value) || 'auto';
    if (this.#autoRows === coerced) return;
    this.#autoRows = coerced;
    this.setAttribute('auto-rows', coerced);
    this.requestUpdate();
  }

  get autoColumns() { return this.#autoColumns; }
  set autoColumns(value) {
    const coerced = value == null ? '' : String(value);
    if (this.#autoColumns === coerced) return;
    this.#autoColumns = coerced;
    if (coerced) { this.setAttribute('auto-columns', coerced); } else { this.removeAttribute('auto-columns'); }
    this.requestUpdate();
  }

  // ── flow ──────────────────────────────────────────────────────────────────
  get flow() { return this.#flow; }
  set flow(value) {
    const raw = value == null ? '' : String(value);
    const coerced = GRID_FLOW_VALUES.includes(raw) ? raw : 'row';
    if (this.#flow === coerced) return;
    this.#flow = coerced;
    this.setAttribute('flow', coerced);
    this.requestUpdate();
  }

  // ── inline ────────────────────────────────────────────────────────────────
  get inline() { return this.#inline; }
  set inline(value) {
    const coerced = Boolean(value);
    if (this.#inline === coerced) return;
    this.#inline = coerced;
    this.toggleAttribute('inline', coerced);
    this.requestUpdate();
  }

  // ── lifecycle ─────────────────────────────────────────────────────────────
  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const skinName = this.#resolveSkin();
      if (skinName === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
    } finally {
      this.#rendering = false;
    }
  }

  #resolveSkin() {
    if (this.#skin) return this.#skin;
    return resolveSkinFromAncestors(this);
  }
}

// ── Gap resolution (shared with skin via _resolvedGap* getters) ─────────────
function _resolveGapValue(value) {
  if (!value) return '';
  if (GRID_GAP_ALIASES.includes(value)) return `var(--run-space-semantic-${value})`;
  if (/^\d+$/.test(value.trim())) return `var(--run-space-${value})`;
  return value;
}

if (!customElements.get('run-grid')) {
  customElements.define('run-grid', RunGrid);
}

export { RunGrid };
export default RunGrid;
